import * as React from 'react'

import { cn } from './cn'

export interface PaginationProps {
  count: number
  page: number
  onChange?: (event: React.SyntheticEvent, page: number) => void
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
  className?: string
}

/** Numbered pagination matching the old MUI component's behavior (siblingCount 1). */
const Pagination = ({
  count,
  page,
  onChange,
  disabled,
  size = 'medium',
  className,
}: PaginationProps) => {
  const pages: (number | '…')[] = []
  const push = (p: number | '…') => pages.push(p)

  const window = 1
  const first = 1
  const last = count

  for (let p = 1; p <= count; p++) {
    if (
      p === first ||
      p === last ||
      (p >= page - window && p <= page + window)
    ) {
      push(p)
    } else if (pages[pages.length - 1] !== '…') {
      push('…')
    }
  }

  const itemSize =
    size === 'small' ? 'h-7 min-w-[28px] text-xs' : 'h-8 min-w-[32px] text-sm'

  const button = (
    label: React.ReactNode,
    target: number,
    opts: { disabled?: boolean; selected?: boolean; aria: string },
  ) => (
    <button
      type="button"
      aria-label={opts.aria}
      aria-current={opts.selected ? 'page' : undefined}
      disabled={disabled || opts.disabled}
      onClick={(e) => onChange?.(e, target)}
      className={cn(
        'inline-flex cursor-pointer items-center justify-center rounded-lg px-1.5 font-medium transition-all duration-150 active:scale-95',
        itemSize,
        opts.selected
          ? 'bg-primary text-primary-contrast shadow-paper'
          : 'text-text-secondary hover:bg-black/[0.05] hover:text-text-primary dark:hover:bg-white/[0.07]',
        'disabled:pointer-events-none disabled:opacity-40',
      )}
    >
      {label}
    </button>
  )

  return (
    <nav aria-label="pagination" className={className}>
      <div className="flex flex-wrap items-center gap-1">
        {button('‹', page - 1, {
          disabled: page <= 1,
          aria: 'Go to previous page',
        })}
        {pages.map((p, i) =>
          p === '…' ? (
            <span
              key={`e${i}`}
              className={cn(
                'px-1.5 text-text-secondary',
                itemSize,
                'inline-flex items-center',
              )}
            >
              …
            </span>
          ) : (
            <React.Fragment key={p}>
              {button(p, p, { selected: p === page, aria: `Go to page ${p}` })}
            </React.Fragment>
          ),
        )}
        {button('›', page + 1, {
          disabled: page >= count,
          aria: 'Go to next page',
        })}
      </div>
    </nav>
  )
}

export default Pagination
